import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/SEO';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const TEAM_VALUES = [
  { icon: 'ri-microscope-line', title: 'Scientific Excellence', desc: 'Every formulation is backed by veterinary research, field trials, and quality testing.' },
  { icon: 'ri-group-line', title: 'Farmer-First Approach', desc: 'We listen to farmers and design solutions that solve real-world livestock challenges.' },
  { icon: 'ri-award-line', title: 'Uncompromising Quality', desc: 'Manufactured under strict pharmaceutical-grade standards by our partner Metsun Pharma.' },
];

const siteUrl = import.meta.env.VITE_SITE_URL || '';

const aboutJsonLd = [
  {
    '@type': 'AboutPage',
    name: 'About Hamva Pharmaceuticals',
    url: `${siteUrl}/about`,
    description: 'Learn about Hamva Pharmaceuticals - 15+ years pioneering animal health innovation in Pakistan.',
  },
  {
    '@type': 'Organization',
    name: 'Hamva Pharmaceuticals',
    url: siteUrl,
    logo: 'https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/f20d0faf8761bd92da69a0386cf62d57.png',
    description: 'Hamva Pharmaceuticals delivers advanced veterinary medicines and animal health solutions across Pakistan.',
    foundingDate: '2009',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '8km Lahore GT Road Rawat',
      addressLocality: 'Rawalpindi',
      addressCountry: 'PK',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+92-300-7321011',
      contactType: 'customer service',
    },
  },
];

export default function About() {
  const { ref: heroRef, isVisible: heroVisible } = useScrollAnimation();
  const { ref: missionRef, isVisible: missionVisible } = useScrollAnimation();
  const { ref: metsunRef, isVisible: metsunVisible } = useScrollAnimation();
  const { ref: valuesRef, isVisible: valuesVisible } = useScrollAnimation();
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  return (
    <div className="min-h-screen bg-dark font-inter text-white">
      <SEO
        title="About Us | Hamva Pharmaceuticals - Pakistan Animal Health"
        description="Learn about Hamva Pharmaceuticals - 15+ years pioneering animal health innovation in Pakistan. Manufactured by Metsun Pharma in Rawalpindi."
        keywords="about hamva pharmaceuticals, veterinary company pakistan, animal health manufacturer, livestock solutions rawalpindi"
        ogTitle="About Hamva Pharmaceuticals"
        ogDescription="Pioneering animal health innovation in Pakistan for over 15 years."
        ogImage="https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/f20d0faf8761bd92da69a0386cf62d57.png"
        canonical={`${siteUrl}/about`}
        jsonLd={aboutJsonLd}
      />
      <Navbar />

      {/* Hero Banner */}
      <section className="relative pt-36 pb-24 px-4 md:px-8 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20pharmaceutical%20manufacturing%20facility%20laboratory%20interior%20clean%20professional%20industrial%20equipment%20dark%20dramatic%20cinematic%20lighting%20quality%20production%20Pakistan&width=1920&height=700&seq=abouthero&orientation=landscape"
            alt="Hamva About"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-dark/80 via-dark/50 to-dark" />
        </div>
        <div
          ref={heroRef as React.RefObject<HTMLElement>}
          className="relative z-10 max-w-7xl mx-auto text-center"
        >
          <div className={`reveal ${heroVisible ? 'visible' : ''}`}>
            <p className="text-accent text-xs font-semibold tracking-[3px] uppercase mb-3">Our Story</p>
            <h1 className="font-poppins font-bold text-4xl md:text-6xl text-white mb-6">
              About Hamva Pharmaceuticals
            </h1>
            <p className="text-text-muted text-lg max-w-2xl mx-auto leading-relaxed">
              Pioneering animal health innovation in Pakistan for over 15 years — dedicated to empowering farmers with world-class veterinary solutions.
            </p>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Brand Story */}
      <section
        ref={missionRef as React.RefObject<HTMLElement>}
        className="py-24 px-4 md:px-8"
        style={{ background: 'linear-gradient(180deg, #0E1218 0%, #101828 100%)' }}
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div className={`reveal ${missionVisible ? 'visible' : ''}`}>
            <div className="relative rounded-2xl overflow-hidden" style={{ height: '460px' }}>
              <img
                src="https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/68bfe5108736179e8c7ed2f6a905da17.png"
                alt="Farmer with livestock"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-dark/60 to-transparent" />
              <div className="absolute bottom-6 left-6 glass rounded-xl px-5 py-4">
                <p className="font-poppins font-bold text-2xl text-accent">15+</p>
                <p className="text-xs text-text-soft mt-0.5">Years of Excellence</p>
              </div>
            </div>
          </div>

          <div className={`reveal reveal-delay-2 ${missionVisible ? 'visible' : ''}`}>
            <p className="text-accent text-xs font-semibold tracking-[3px] uppercase mb-3">Who We Are</p>
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-6 leading-snug">
              Committed to Advancing<br />Animal Health in Pakistan
            </h2>
            <p className="text-text-muted text-base leading-relaxed mb-6">
              Hamva Pharmaceuticals was founded with a single purpose: to give Pakistani farmers access to the best veterinary products in the world. Since our inception, we have been developing, sourcing, and distributing high-performance animal health solutions that deliver real, measurable results.
            </p>
            <p className="text-text-muted text-base leading-relaxed mb-8">
              From dairy farms in Punjab to livestock operations in Sindh and KPK, our products have become the trusted choice of thousands of farmers and distributors nationwide. We believe that healthy animals mean prosperous farmers — and prosperous farmers mean a stronger Pakistan.
            </p>

            {/* Mission & Vision cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="rounded-xl p-5 border border-accent/20" style={{ background: '#131923' }}>
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent/15 mb-3">
                  <i className="ri-focus-3-line text-accent text-lg" />
                </div>
                <h4 className="font-poppins font-bold text-white text-sm mb-2">Our Mission</h4>
                <p className="text-text-muted text-xs leading-relaxed">
                  To improve livestock health and farmer outcomes across Pakistan through innovative, affordable veterinary solutions.
                </p>
              </div>
              <div className="rounded-xl p-5 border border-accent/20" style={{ background: '#131923' }}>
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent/15 mb-3">
                  <i className="ri-eye-line text-accent text-lg" />
                </div>
                <h4 className="font-poppins font-bold text-white text-sm mb-2">Our Vision</h4>
                <p className="text-text-muted text-xs leading-relaxed">
                  To become the most trusted name in veterinary pharmaceuticals across South Asia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Manufacturing Partner — Metsun */}
      <section
        ref={metsunRef as React.RefObject<HTMLElement>}
        className="py-24 px-4 md:px-8"
        style={{ background: '#0A0F1A' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className={`rounded-3xl overflow-hidden reveal ${metsunVisible ? 'visible' : ''}`} style={{ background: '#131923' }}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              {/* Image side */}
              <div className="relative" style={{ minHeight: '360px' }}>
                <img
                  src="https://static.readdy.ai/image/a84fce21c15147349888dd3eeb9b8000/402d1e4bac988018d88d2425f8408862.jpeg"
                  alt="Metsun Manufacturing"
                  className="w-full h-full object-cover object-top"
                  style={{ minHeight: '360px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-dark/60 lg:bg-gradient-to-r" />
              </div>

              {/* Text side */}
              <div className="p-10 lg:p-14 flex flex-col justify-center">
                <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/25 rounded-full px-4 py-1.5 mb-6 w-fit">
                  <i className="ri-building-4-line text-accent text-sm" />
                  <span className="text-accent text-xs font-semibold tracking-wider uppercase">Manufacturing Partner</span>
                </div>
                <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
                  Manufactured by <span className="text-accent">Metsun</span>
                </h2>
                <p className="text-text-muted text-base leading-relaxed mb-6">
                  Our products are manufactured by <strong className="text-text-soft">Metsun Pharma</strong>, a reputable pharmaceutical manufacturing company located at 8km Lahore GT Road, Rawat, Rawalpindi. Metsun Pharma brings state-of-the-art production facilities, rigorous quality control, and decades of experience in pharmaceutical manufacturing.
                </p>
                <div className="space-y-3">
                  {[
                    { icon: 'ri-map-pin-line', text: '8km Lahore GT Road, Rawat, Rawalpindi, Pakistan' },
                    { icon: 'ri-shield-check-line', text: 'GMP-compliant manufacturing' },
                    { icon: 'ri-flask-line', text: 'In-house quality control labs' },
                    { icon: 'ri-award-line', text: 'Certified pharmaceutical standards' },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-accent`} />
                      </div>
                      <span className="text-text-soft text-sm">{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section
        ref={valuesRef as React.RefObject<HTMLElement>}
        className="py-24 px-4 md:px-8"
        style={{ background: '#0E1218' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className={`text-center mb-14 reveal ${valuesVisible ? 'visible' : ''}`}>
            <p className="text-accent text-xs font-semibold tracking-[3px] uppercase mb-3">What Drives Us</p>
            <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white">Our Core Values</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TEAM_VALUES.map((val, i) => (
              <div
                key={i}
                className={`group rounded-2xl p-8 border border-white/5 hover:border-accent/30 transition-all duration-300 text-center reveal reveal-delay-${i + 1} ${valuesVisible ? 'visible' : ''}`}
                style={{ background: '#131923' }}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-accent/10 mx-auto mb-6 group-hover:bg-accent/20 transition-colors duration-300">
                  <i className={`${val.icon} text-accent text-2xl`} />
                </div>
                <h3 className="font-poppins font-bold text-xl text-white mb-3">{val.title}</h3>
                <p className="text-text-muted text-sm leading-relaxed">{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        ref={ctaRef as React.RefObject<HTMLElement>}
        className="py-20 px-4 md:px-8"
        style={{ background: 'linear-gradient(135deg, #8B1428 0%, #C41E3A 50%, #8B1428 100%)' }}
      >
        <div className={`max-w-3xl mx-auto text-center reveal ${ctaVisible ? 'visible' : ''}`}>
          <h2 className="font-poppins font-bold text-3xl md:text-4xl text-white mb-4">
            Partner with Hamva Today
          </h2>
          <p className="text-white/70 text-base mb-8 max-w-lg mx-auto">
            Whether you&apos;re a farmer, veterinarian, or distributor — we&apos;re here to support your animal health goals.
          </p>
          <a
            href="https://wa.me/923007321011"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-9 py-4 rounded-full bg-white text-accent font-poppins font-bold text-base cursor-pointer whitespace-nowrap hover:bg-white/90 transition-all duration-300"
          >
            <i className="ri-whatsapp-line text-xl" />
            WhatsApp Us
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
